import {Evented} from 'maplibre-gl';
import {createElement} from '../helpers/helpers';

const DATE_FORMAT = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short'
};

// 스피너 버튼에 붙일 접근 가능한 이름. 사전 11종에는 날짜 단위 키가 없고, 없는 번역을
// 지어내는 대신 플랫폼이 로캘별로 주는 이름을 쓴다 (ko 년/월/일/시/분/초, ja 年/月/日...).
// ICU 가 모르는 로캘은 영어로 떨어지는데, 이름이 없는 것보다는 낫다.
const fieldName = (lang, id) => {
    try {
        return new Intl.DisplayNames(lang, {type: 'dateTimeField'}).of(id);
    } catch (e) {
        return id;
    }
};

const DATE_COMPONENTS = [
    {id: 'year', fn: 'FullYear', digits: 4, extra: 0},
    {id: 'month', fn: 'Month', digits: 2, extra: 1},
    {id: 'day', fn: 'Date', digits: 2, extra: 0},
    {id: 'hour', fn: 'Hours', digits: 2, extra: 0},
    {id: 'minute', fn: 'Minutes', digits: 2, extra: 0},
    {id: 'second', fn: 'Seconds', digits: 2, extra: 0}
];

export default class extends Evented {

    constructor(options) {
        super();

        const me = this;

        me._lang = options.lang;
        me._dict = options.dict;
        me._clock = options.clock;
        me._mode = options.mode || 'realtime';
    }

    getDefaultPosition() {
        return 'top-left';
    }

    onAdd(map) {
        const me = this;

        me._map = map;

        me._container = createElement('div', {className: 'maplibregl-ctrl ctrl-group'});
        me._element = createElement('div', {className: 'clock-ctrl'}, me._container);
        me._update();

        (function repeat() {
            const now = me._clock.getTime();

            if (Math.floor(now / 1000) !== Math.floor(me._lastRefresh / 1000)) {
                me._refresh();
                me._refreshFreshness();
                me._lastRefresh = now;
            }
            if (me._container) {
                requestAnimationFrame(repeat);
            }
        })();

        return me._container;
    }

    onRemove() {
        const me = this,
            container = me._container;

        container.parentNode.removeChild(container);
        delete me._container;
        delete me._map;
    }

    /**
     * Sets the number of trains currently running, shown under the clock.
     * @param {number} count - Number of running trains
     */
    setTrainCount(count) {
        const me = this,
            element = me._container && me._container.querySelector('#train-count');

        me._trainCount = count;
        if (element) {
            element.innerHTML = count ? me._dict['trains-running'].replace('$1', count) : '';
        }
    }

    /**
     * 실시간 피드의 신선도. 주기는 **관측 간격**으로, 기점은 **받은 시각**으로 잰다.
     *
     * ⚠️ 주기를 상수로 박지 않는다 - 노선별 표본주기(`routes.json` 의 `every`)와
     * 쿼터 사정에 따라 바뀌고, 문서만 고치고 코드가 남는 부류의 값이다.
     * @param {number} observedAt - 가장 새 관측의 시각 (ms). 없으면 표시하지 않는다
     * @param {number} period - 관측 사이 간격 (ms). 없으면 나이만 보여준다
     * @param {number} receivedAt - 그 관측을 받은 시각 (ms). 카운트다운의 기점이다
     */
    setFeedFreshness(observedAt, period, receivedAt) {
        const me = this;

        me._observedAt = observedAt;
        me._feedPeriod = period;
        // 카운트다운은 **받은 시각**부터 잰다 (map.js 참조). 관측 시각부터 재면
        // 피드 재작성·재읽기 지연만큼 0 에서 멈춰 있다.
        me._receivedAt = receivedAt;
        me._refreshFreshness();
    }

    _refreshFreshness() {
        const me = this,
            element = me._container && me._container.querySelector('#feed-age');

        if (!element) {
            return;
        }
        if (!me._observedAt || me._mode !== 'realtime') {
            element.innerHTML = '';
            return;
        }

        const dict = me._dict,
            age = Math.max(0, Math.round((me._clock.getTime() - me._observedAt) / 1000));

        // 첫 접속에는 주기를 아직 못 쟀다. 빈칸으로 두면 "이 지도 죽었나" 가 되므로
        // 그동안은 **나이**를 보여준다 - 셋 다 한 번에 한 숫자만 말한다.
        if (!me._feedPeriod) {
            element.innerHTML = dict['feed-age'].replace('$1', age);
            return;
        }

        const period = Math.round(me._feedPeriod / 1000),
            since = Math.max(0, Math.round((Date.now() - me._receivedAt) / 1000)),
            next = period - since,
            // 한 주기를 통째로 넘겼으면 기다리는 게 아니라 **멈춘** 것이다.
            // 이 지도는 신선도가 곧 제품이라 그 상태를 말해 줘야 한다.
            // '멈춤' 판정만 관측 나이로 본다 - 그건 '데이터가 늙었나' 라서
            // 화면이 언제 받았는지가 아니라 원천이 언제 봤는지가 답이다.
            key = next > 0 ? 'feed-next' : age > period * 2 ? 'feed-stale' : 'feed-due';

        element.innerHTML = dict[key].replace('$1', age).replace('$2', next);
    }

    setMode(mode) {
        const me = this;

        me._mode = mode;
        delete me._tempDate;
        me._editing = false;
        me._update();
    }

    _onChange() {
        const me = this;

        me.fire({type: 'change', clock: me._clock});
    }

    _update() {
        const me = this,
            element = me._element,
            dict = me._dict,
            clock = me._clock,
            mode = me._mode,
            editing = me._editing;

        // 실시간에서는 한 줄로 줄인다 — 날짜는 재생 모드에서만 의미가 있고, 세 줄짜리
        // 카드가 하단 바에서 자리를 크게 먹는다. 요소 id 는 _refresh 가 찾으므로 유지하고
        // 날짜는 CSS 로 감춘다.
        element.className = `clock-ctrl clock-${mode === 'playback' ? 'playback' : 'realtime'}`;
        element.innerHTML = [
            mode === 'realtime' || !editing ?
                '<div class="clock-line"><span id="date"></span><span id="time"></span><span id="train-count" class="train-count"></span><span id="feed-age" class="feed-age"></span></div>' : '',
            mode === 'playback' && !editing ? [
                '<div class="clock-button">',
                `<span><button id="edit-time-button">${dict['edit-date-time']}</button></span>`,
                '</div>'
            ].join('') : '',
            mode === 'playback' && editing ? [
                '<div class="clock-controller">',
                DATE_COMPONENTS.slice(0, 3).map(({id}) => [
                    `<span class="spin-box" role="group" aria-label="${fieldName(me._lang, id)}">`,
                    `<div><button id="${id}-increase-button" class="top-button" aria-label="${fieldName(me._lang, id)} +1"><span class="increase-icon"></span></button></div>`,
                    `<div id="${id}"></div>`,
                    `<div><button id="${id}-decrease-button" class="bottom-button" aria-label="${fieldName(me._lang, id)} -1"><span class="decrease-icon"></span></button></div>`,
                    '</span>'
                ].join('')).join('<span class="clock-controller-separator">-</span>'),
                '<span class="clock-controller-separator"></span>',
                DATE_COMPONENTS.slice(-3).map(({id}) => [
                    `<span class="spin-box" role="group" aria-label="${fieldName(me._lang, id)}">`,
                    `<div><button id="${id}-increase-button" class="top-button" aria-label="${fieldName(me._lang, id)} +1"><span class="increase-icon"></span></button></div>`,
                    `<div id="${id}"></div>`,
                    `<div><button id="${id}-decrease-button" class="bottom-button" aria-label="${fieldName(me._lang, id)} -1"><span class="decrease-icon"></span></button></div>`,
                    '</span>'
                ].join('')).join('<span class="clock-controller-separator">:</span>'),
                '</div>',
                '<div class="clock-button">',
                `<span><button id="edit-time-cancel-button">${dict['cancel']}</button></span>`,
                '<span class="clock-controller-separator"></span>',
                `<span><button id="edit-time-ok-button" disabled>${dict['ok']}</button></span>`,
                '</div>'
            ].join('') : '',
            mode === 'playback' ? [
                '<div class="speed-controller">',
                `<span><button id="speed-decrease-button" class="left-button" aria-label="${dict['x-speed']} -"`,
                clock.speed === 1 ? ' disabled' : '',
                '><span class="decrease-icon"></span></button></span>',
                `<span id="clock-speed">${clock.speed}${dict['x-speed']}</span>`,
                `<span><button id="speed-increase-button" class="right-button" aria-label="${dict['x-speed']} +"`,
                clock.speed === 600 ? ' disabled' : '',
                '><span class="increase-icon"></span></button></span>',
                '</div>'
            ].join('') : ''
        ].join('');

        me._refresh();
        me.setTrainCount(me._trainCount);
        me._refreshFreshness();

        if (mode === 'playback' && editing) {
            element.querySelector('#edit-time-cancel-button').addEventListener('click', () => {
                delete me._tempDate;
                me._editing = false;
                me._update();
            });
            element.querySelector('#edit-time-ok-button').addEventListener('click', () => {
                const date = me._tempDate;

                if (date) {
                    clock.setDate(date);
                    me._onChange();
                    delete me._tempDate;
                }

                me._editing = false;
                me._update();
            });
        }

        if (mode === 'playback' && !editing) {
            element.querySelector('#edit-time-button').addEventListener('click', () => {
                me._editing = true;
                me._update();
            });
        }

        if (mode === 'playback' && editing) {
            for (const {id, fn} of DATE_COMPONENTS) {
                element.querySelector(`#${id}-increase-button`).addEventListener('click', () => {
                    const date = me._tempDate = me._tempDate || clock.getDate();

                    date[`set${fn}`](date[`get${fn}`]() + 1);
                    me._refresh();
                });
                element.querySelector(`#${id}-decrease-button`).addEventListener('click', () => {
                    const date = me._tempDate = me._tempDate || clock.getDate();

                    date[`set${fn}`](date[`get${fn}`]() - 1);
                    me._refresh();
                });
            }
        }

        if (mode === 'playback') {
            element.querySelector('#speed-increase-button').addEventListener('click', e => {
                let speed = clock.speed;

                speed += speed < 10 ? 1 : speed < 100 ? 10 : 100;
                clock.setSpeed(speed);
                e.currentTarget.disabled = speed === 600;
                element.querySelector('#speed-decrease-button').disabled = false;
                element.querySelector('#clock-speed').innerHTML = speed + dict['x-speed'];
            });
            element.querySelector('#speed-decrease-button').addEventListener('click', e => {
                let speed = clock.speed;

                speed -= speed <= 10 ? 1 : speed <= 100 ? 10 : 100;
                clock.setSpeed(speed);
                e.currentTarget.disabled = speed === 1;
                element.querySelector('#speed-increase-button').disabled = false;
                element.querySelector('#clock-speed').innerHTML = speed + dict['x-speed'];
            });
        }
    }

    _refresh() {
        const me = this,
            element = me._element;
        let date = me._clock.getDate();

        if (!me._editing) {
            const lang = me._lang;
            const dateString = date.toLocaleDateString(lang, DATE_FORMAT);

            element.querySelector('#date').innerHTML = dateString;
            element.querySelector('#time').innerHTML = date.toLocaleTimeString(lang);
        } else {
            const tempDate = me._tempDate;

            if (tempDate) {
                date = tempDate;
                for (const {id} of DATE_COMPONENTS) {
                    element.querySelector(`#${id}`).classList.add('desc-caution');
                }
                element.querySelector('#edit-time-ok-button').disabled = false;
            }
            for (const {id, fn, digits, extra} of DATE_COMPONENTS) {
                element.querySelector(`#${id}`).innerHTML =
                    `0${date[`get${fn}`]() + extra}`.slice(-digits);
            }
        }
    }
}
